import type { CreateProps } from "react-admin";
import { Create, SimpleForm, DateInput, ReferenceInput, SelectInput, TextInput } from "react-admin";
import { FormDataConsumer } from "react-admin";
import Typography from "@mui/material/Typography";
import { purposeHelperText } from "@/utils/purpose-helper-text";
import RazaItsLookup from "./components/RazaItsLookup";
import RazaSafaiUploadField from "./components/RazaSafaiUploadField";

const validateMohallah = (value: unknown, allValues?: Record<string, unknown>) => {
  const matched = allValues?.itsDirectoryMatched;
  if (matched === false) {
    const mohallah = typeof value === "string" ? value.trim() : "";
    if (!mohallah) return "Mohallah is required when ITS is not in directory";
  }
  return undefined;
};

const transform = (data: Record<string, unknown>) => {
  const requestDateRaw = data.requestDate;
  const requestDate =
    requestDateRaw instanceof Date ? requestDateRaw : new Date(String(requestDateRaw ?? ""));
  const payload: Record<string, unknown> = {
    purpose: data.purpose,
    itsNo: data.itsNo,
    name: data.name,
    mohallah: data.mohallah,
  };
  if (!Number.isNaN(requestDate.getTime())) {
    payload.requestDate = requestDate.toISOString();
  }
  if (data.itsDirectoryMatched === false) {
    const safaiUrl = typeof data.safaiUrl === "string" ? data.safaiUrl.trim() : "";
    if (safaiUrl) {
      payload.safaiUrl = safaiUrl;
    }
  }
  return payload;
};

const defaultValues = () => ({
  requestDate: new Date(),
  itsDirectoryMatched: true,
});

const RazaRequestsCreate = (props: CreateProps) => (
  <Create {...props} transform={transform} redirect="show">
    <SimpleForm warnWhenUnsavedChanges sx={{ maxWidth: 700 }} defaultValues={defaultValues}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }} gutterBottom>
        Request information
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Who is requesting raza, for which purpose, and when the request was filed.
      </Typography>
      <RazaItsLookup source="itsNo" label="ITS no." fullWidth isRequired />
      <TextInput source="name" label="Name" fullWidth required />
      <FormDataConsumer>
        {({ formData }) =>
          formData?.itsDirectoryMatched === false ? (
            <>
              <TextInput
                source="mohallah"
                label="Mohallah"
                fullWidth
                validate={validateMohallah}
                required
              />
              <RazaSafaiUploadField />
            </>
          ) : null
        }
      </FormDataConsumer>
      <ReferenceInput
        source="purpose"
        reference="bookingPurpose"
        label="Purpose"
        fullWidth
        required
      >
        <SelectInput
          optionText="name"
          optionValue="id"
          fullWidth
          helperText={purposeHelperText("razaRequest")}
        />
      </ReferenceInput>
      <DateInput source="requestDate" label="Request date" fullWidth required />
    </SimpleForm>
  </Create>
);

export default RazaRequestsCreate;
